import React, { useState, useEffect, useCallback } from 'react';
import { User, Mail, Building2, Shield, Briefcase, Camera, Check, ChevronLeft, ChevronRight, ChevronDown, RefreshCw, Search, X } from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';

/* ════════════════════════════════════════════════════════════════════
   Éléments de présentation uniquement (aucune logique métier ici)
   ════════════════════════════════════════════════════════════════════ */

const cx = (...c) => c.filter(Boolean).join(' ');

const controlCls =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 transition placeholder:text-slate-400 hover:border-slate-400 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500 read-only:cursor-not-allowed read-only:bg-slate-50 read-only:text-slate-600';

const btnPrimary =
  'inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60';

const btnSecondary =
  'inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40 disabled:cursor-not-allowed disabled:opacity-50';

const Field = ({ label, required, hint, htmlFor, className = '', children }) => (
  <div className={className}>
    {label && (
      <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}
        {required && <span className="ml-0.5 text-red-600" aria-hidden="true">*</span>}
      </label>
    )}
    {children}
    {hint && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>}
  </div>
);

const Input = ({ label, hint, className, ...props }) => (
  <Field label={label} required={props.required} hint={hint} htmlFor={props.name} className={className}>
    <input id={props.name} className={controlCls} {...props} />
  </Field>
);

const Textarea = ({ label, hint, className, ...props }) => (
  <Field label={label} required={props.required} hint={hint} htmlFor={props.name} className={className}>
    <textarea id={props.name} className={controlCls} {...props} />
  </Field>
);

const SelectControl = ({ children, ...props }) => (
  <div className="relative">
    <select className={cx(controlCls, 'appearance-none pr-9')} {...props}>{children}</select>
    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
  </div>
);

const Select = ({ label, hint, className, children, ...props }) => (
  <Field label={label} required={props.required} hint={hint} htmlFor={props.name} className={className}>
    <SelectControl id={props.name} {...props}>{children}</SelectControl>
  </Field>
);

const SearchBox = (props) => (
  <div className="relative">
    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
    <input type="text" className={cx(controlCls, 'pl-9')} {...props} />
  </div>
);

// Choix à deux options : appelle simplement onSelect(valeur)
const Segmented = ({ label, required, value, options, onSelect, className }) => (
  <Field label={label} required={required} className={className}>
    <div role="radiogroup" aria-label={label} className="flex rounded-lg bg-slate-100 p-1">
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onSelect(o.value)}
            className={cx(
              'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40',
              active ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-800',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  </Field>
);

const Avatar = ({ src, name, className = '' }) => {
  const initials = String(name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
  return src ? (
    <img src={src} alt="" className={cx('rounded-full object-cover', className)} />
  ) : (
    <span className={cx('flex items-center justify-center rounded-full bg-blue-100 font-semibold text-blue-700', className)}>
      {initials || <User className="h-1/2 w-1/2" />}
    </span>
  );
};

// Titre et explication à gauche, champs à droite
const Section = ({ title, description, children }) => (
  <section className="grid gap-x-10 gap-y-4 border-t border-slate-200 py-7 first:border-t-0 first:pt-1 md:grid-cols-[190px_minmax(0,1fr)]">
    <div>
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      {description && <p className="mt-1 text-sm leading-snug text-slate-500">{description}</p>}
    </div>
    <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">{children}</div>
  </section>
);

const SummaryBlock = ({ title, children }) => (
  <section className="grid gap-x-10 gap-y-3 border-t border-slate-200 py-6 first:border-t-0 first:pt-1 md:grid-cols-[190px_minmax(0,1fr)]">
    <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
    <dl className="grid grid-cols-1 gap-x-5 gap-y-3 sm:grid-cols-2">{children}</dl>
  </section>
);

const SummaryRow = ({ label, children }) => (
  <div className="min-w-0">
    <dt className="text-xs text-slate-500">{label}</dt>
    <dd className="mt-0.5 break-words text-sm font-medium text-slate-900">{children}</dd>
  </div>
);

const PersonnelWizard = ({ onClose, onSuccess, editData = null, isModal = true }) => {
  const { filialeId, currentFiliale } = useFiliale();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [managerSearch, setManagerSearch] = useState('');
  const [n2Search, setN2Search] = useState('');
  const [formData, setFormData] = useState({
    // Étape 1 - Identité
    nom: '',
    prenom: '',
    autres_prenom: '',
    sexe: '',
    date_naissance: '',
    lieu_naissance: '',
    nationalite: 'Béninoise',
    type_piece_id: '',
    num_piece_id: '',
    lieu_delivrance_piece: '',
    date_expiration_piece: '',
    numtel: '',
    adressemail: '',
    adresse_complete: '',
    personne_contact: '',
    numero_cnss: '',
    part_igr: '',
    grille_personnel_id: '',
    date_entree: '',
    departement_id: '',
    business_unit_id: '',
    titre_id: '',
    grade_id: '',
    statut: '',
    type_contrat: '',
    type_personnel_id: '',
    activite_id: '',
    idcampagne: '',
    marque_id: '',
    emplacement: '',
    manager_hierarchique_id: '',
    fonction_manager: '',
    n2_id: '',
    domaine_etude: '',
    niveau_etude: '',
    domiciliation: 'Non',
    numero_ifu: '',
    situation_matrimoniale: '',
    nombre_enfant_charge: '',
    direct_indirect: false, // false = Indirect (par défaut), true = Direct
  });

  const [dropdownData, setDropdownData] = useState({
    departements: [],
    businessUnits: [],
    grades: [],
    titres: [],
    typesContrat: [],
    typesPersonnel: [],
    marques: [],
    clients: [],
    campagnes: [],
    activites: [],
    managers: [],
    niveauxEtude: [],
    grillesPersonnel: [],
  });

  const [filteredCampagnes, setFilteredCampagnes] = useState([]);
  const [photoFile, setPhotoFile] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [autoGenerateMatricule, setAutoGenerateMatricule] = useState(false);

  const filteredManagers = dropdownData.managers?.filter(m =>
    `${m.prenom} ${m.nom}`.toLowerCase().includes(managerSearch.toLowerCase())
  ) || [];

  const filteredN2 = dropdownData.managers?.filter(m =>
    `${m.prenom} ${m.nom}`.toLowerCase().includes(n2Search.toLowerCase())
  ) || [];

  // Charger les données au montage et quand la filiale change
  useEffect(() => {
    fetchDropdownData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filialeId]);

  // Initialiser formData avec editData (une seule fois)
  useEffect(() => {
    if (editData && !isInitialized) {
      console.log('EditData direct_indirect original:', editData.direct_indirect, typeof editData.direct_indirect);
      // Convertir les valeurs null en chaînes vides pour éviter les warnings React
      const sanitizedEditData = {};
      Object.keys(editData).forEach(key => {
        let value = editData[key];
        // Convertir direct_indirect de nombre (0/1) en booléen (true/false) pour l'interrupteur
        if (key === 'direct_indirect') {
          // Si la valeur est 0 ou 1, convertir en booléen
          if (value === 0 || value === 1) {
            value = value === 1;
          } else if (value === null || value === undefined || value === '') {
            // Valeur par défaut: false (Indirect)
            value = false;
          } else if (value === 'true' || value === true) {
            value = true;
          } else if (value === 'false' || value === false) {
            value = false;
          }
          console.log('EditData direct_indirect converted:', value, typeof value);
          sanitizedEditData[key] = value;
        } else {
          sanitizedEditData[key] = value === null || value === undefined ? '' : value;
        }
      });
      setFormData({ ...formData, ...sanitizedEditData });
      setIsInitialized(true);
      setAutoGenerateMatricule(false); // Désactiver l'auto-génération lors de l'édition

      // Initialiser les champs de recherche pour manager et N+2
      if (editData.manager_hierarchique_id && dropdownData.managers) {
        const manager = dropdownData.managers.find(m => m.idpersonnel === editData.manager_hierarchique_id);
        if (manager) setManagerSearch(`${manager.prenom} ${manager.nom}`);
      }
      if (editData.n2_id && dropdownData.managers) {
        const n2 = dropdownData.managers.find(m => m.idpersonnel === editData.n2_id);
        if (n2) setN2Search(`${n2.prenom} ${n2.nom}`);
      }
    } else {
      // Initialiser filiale_id avec la filiale courante si pas de mode édition
      setFormData(prev => ({ ...prev, filiale_id: filialeId || '' }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filialeId, editData]);

  const fetchDropdownData = useCallback(async () => {
    try {
      const [deps, businessUnits, grades, titles, contrats, typesPers, marques, clients, campagnes, activites, personnel, niveaux, filiales, grilles] = await Promise.all([
        http.get('/api/departements'),
        http.get('/api/business-units'),
        http.get('/api/grades'),
        http.get('/api/titres' + (filialeId ? `?filiale_id=${filialeId}` : '')),
        http.get('/api/types-contrat'),
        http.get('/api/types-personnel' + (filialeId ? `?filiale_id=${filialeId}` : '')),
        http.get('/api/marques'),
        http.get('/api/clients'),
        http.get('/api/campagnes' + (filialeId ? `?filiale_id=${filialeId}` : '')),
        http.get('/api/activites'),
        http.get('/api/personnel'),
        http.get('/api/niveaux-etude'),
        http.get('/api/filiales'),
        http.get('/api/grille-personnel' + (filialeId ? `?filiale_id=${filialeId}` : ''))
      ]);
      setDropdownData({
        departements: deps.data || [],
        businessUnits: businessUnits.data || [],
        grades: grades.data || [],
        titres: titles.data || [],
        typesContrat: contrats.data || [],
        typesPersonnel: typesPers.data || [],
        marques: marques.data || [],
        clients: clients.data || [],
        campagnes: campagnes.data || [],
        activites: activites.data || [],
        managers: personnel.data?.data || [],
        niveauxEtude: niveaux.data || [],
        filiales: filiales.data || [],
        grillesPersonnel: Array.isArray(grilles.data) ? grilles.data : grilles || []
      });
      setFilteredCampagnes(campagnes.data || []);
    } catch (error) {
      console.error('Erreur chargement données:', error);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Filtrer les campagnes par activité sélectionnée
    if (name === 'activite_id' && value) {
      const filtered = dropdownData.campagnes.filter(c => c.idactivites == value);
      setFilteredCampagnes(filtered);
      // Réinitialiser idcampagne si l'activité change
      setFormData(prev => ({ ...prev, idcampagne: '' }));
    }

    // Le statut du personnel alimente type_contrat
    if (name === 'statut' && value) {
      setFormData(prev => ({ ...prev, type_contrat: value }));
    }

    // Générer matricule automatiquement si activé et filiale/direct_indirect change
    if (autoGenerateMatricule && (name === 'filiale_id' || name === 'direct_indirect')) {
      generateMatricule();
    }

    // Si le champ modifié est manager_hierarchique_id, mettre à jour le champ de recherche
    if (name === 'manager_hierarchique_id') {
      const selectedManager = dropdownData.managers?.find(m => m.idpersonnel === parseInt(value));
      if (selectedManager) {
        setManagerSearch(`${selectedManager.prenom} ${selectedManager.nom}`);
      } else {
        setManagerSearch('');
      }
    }

    // Si le champ modifié est n2_id, mettre à jour le champ de recherche
    if (name === 'n2_id') {
      const selectedN2 = dropdownData.managers?.find(m => m.idpersonnel === parseInt(value));
      if (selectedN2) {
        setN2Search(`${selectedN2.prenom} ${selectedN2.nom}`);
      } else {
        setN2Search('');
      }
    }

    // Calcul automatique des parts IGR basé sur situation matrimoniale et nombre d'enfants
    if (name === 'situation_matrimoniale' || name === 'nombre_enfant_charge') {
      const situation = name === 'situation_matrimoniale' ? value : formData.situation_matrimoniale;
      const enfants = name === 'nombre_enfant_charge' ? parseInt(value) || 0 : parseInt(formData.nombre_enfant_charge) || 0;
      const parts = calculerPartsIGR(situation, enfants);
      setFormData(prev => ({ ...prev, part_igr: parts }));
    }
  };

  // Fonction pour générer le matricule automatiquement selon la nomenclature
  const generateMatricule = async () => {
    try {
      if (!formData.filiale_id) {
        console.warn('Impossible de générer le matricule: filiale non sélectionnée');
        return;
      }

      const response = await http.post('/api/nomenclature-matricule/generer', {
        filiale_id: formData.filiale_id,
        direct_indirect: formData.direct_indirect === true
      });

      if (response.data && response.data.matricule) {
        setFormData(prev => ({ ...prev, matricule: response.data.matricule }));
        console.log('Matricule généré:', response.data.matricule);
      }
    } catch (error) {
      console.error('Erreur génération matricule:', error);
    }
  };

  // Calcul des parts IGR selon la grille ivoirienne
  const calculerPartsIGR = (situation, enfants) => {
    const enfant = parseInt(enfants) || 0;
    const partsBase = 1; // Part de base pour célibataire/divorcé/veuf sans enfant

    if (!situation) return partsBase;

    const situationLower = situation.toLowerCase();

    // Célibataire, divorcé ou veuf sans enfant
    if ((situationLower.includes('célibataire') || situationLower.includes('divorcé') || situationLower.includes('veuf')) && enfant === 0) {
      return partsBase;
    }

    // Marié sans enfant
    if (situationLower.includes('marié') && enfant === 0) {
      return 2;
    }

    // Célibataire ou divorcé avec 1 enfant
    if ((situationLower.includes('célibataire') || situationLower.includes('divorcé')) && enfant === 1) {
      return 2;
    }

    // Marié ou veuf avec 1 enfant
    if ((situationLower.includes('marié') || situationLower.includes('veuf')) && enfant === 1) {
      return 2.5;
    }

    // Célibataire ou divorcé avec 2 enfants
    if ((situationLower.includes('célibataire') || situationLower.includes('divorcé')) && enfant === 2) {
      return 2.5;
    }

    // Marié ou veuf avec 2 enfants
    if ((situationLower.includes('marié') || situationLower.includes('veuf')) && enfant === 2) {
      return 3;
    }

    // Célibataire ou divorcé avec 3 enfants
    if ((situationLower.includes('célibataire') || situationLower.includes('divorcé')) && enfant === 3) {
      return 3;
    }

    // Marié ou veuf avec 3 enfants
    if ((situationLower.includes('marié') || situationLower.includes('veuf')) && enfant === 3) {
      return 3.5;
    }

    // Célibataire ou divorcé avec 4 enfants
    if ((situationLower.includes('célibataire') || situationLower.includes('divorcé')) && enfant === 4) {
      return 3.5;
    }

    // Marié ou veuf avec 4 enfants
    if ((situationLower.includes('marié') || situationLower.includes('veuf')) && enfant === 4) {
      return 4;
    }

    // Pour 5 enfants et plus : +0.5 par enfant supplémentaire, plafond 5
    let parts = 0;
    if (situationLower.includes('marié') || situationLower.includes('veuf')) {
      parts = 3.5 + (enfant - 3) * 0.5; // À partir de 3 enfants
    } else {
      parts = 2.5 + (enfant - 2) * 0.5; // À partir de 2 enfants
    }

    return Math.min(parts, 5); // Plafond à 5 parts
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, photo_profil: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleNext = () => {
    if (currentStep < 6) setCurrentStep(currentStep + 1);
  };

  const handlePrevious = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Si une photo est présente, utiliser FormData
      if (photoFile || formData.photo_profil) {
        const data = new FormData();
        Object.keys(formData).forEach(key => {
          if (key !== 'photo_profil') {
            // Convertir direct_indirect en nombre
            if (key === 'direct_indirect') {
              data.append(key, formData[key] === true ? 1 : formData[key] === false ? 0 : formData[key]);
            } else {
              data.append(key, formData[key]);
            }
          }
        });

        if (photoFile) {
          data.append('photo_profil', photoFile);
        } else if (formData.photo_profil && !formData.photo_profil.startsWith('data:')) {
          // Si c'est une URL existante, la garder
          data.append('photo_profil', formData.photo_profil);
        }

        if (editData) {
          await http.put(`/api/personnel/${editData.idpersonnel}`, data, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } else {
          const response = await http.post('/api/personnel', data, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          onSuccess(response.data.id);
        }
      } else {
        // Sans photo, envoi JSON normal
        const submitData = {
          ...formData,
          direct_indirect: formData.direct_indirect === true ? 1 : formData.direct_indirect === false ? 0 : formData.direct_indirect
        };
        if (editData) {
          await http.put(`/api/personnel/${editData.idpersonnel}`, submitData);
        } else {
          const response = await http.post('/api/personnel', submitData);
          onSuccess(response.data.id);
        }
      }

      if (editData) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { id: 1, title: 'Identité', icon: User },
    { id: 2, title: 'Coordonnées', icon: Mail },
    { id: 3, title: 'Affectation', icon: Building2 },
    { id: 4, title: 'Hiérarchie', icon: Shield },
    { id: 5, title: 'Compléments', icon: Briefcase },
    { id: 6, title: 'Validation', icon: Check },
  ];

  // Textes d'en-tête de chaque étape (présentation uniquement)
  const stepInfo = {
    1: { title: "Informations d'identité", subtitle: "Renseignez les informations personnelles de l'employé" },
    2: { title: 'Coordonnées de contact', subtitle: "Renseignez les informations de contact de l'employé" },
    3: { title: 'Affectation organisationnelle', subtitle: "Définissez l'affectation de l'employé dans l'entreprise" },
    4: { title: 'Structure hiérarchique', subtitle: "Définissez la hiérarchie et les relations de l'employé" },
    5: { title: 'Informations complémentaires', subtitle: "Renseignez les informations additionnelles de l'employé" },
    6: { title: 'Validation des informations', subtitle: 'Vérifiez les informations avant de sauvegarder' },
  };

  const fullName = [formData.prenom, formData.nom].filter(Boolean).join(' ');

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <Section title="Identité" description="Les informations personnelles de l'employé.">
              <div className="flex items-center gap-4 sm:col-span-2">
                <div className="h-20 w-20 shrink-0 overflow-hidden rounded-full">
                  <Avatar src={formData.photo_profil} name={fullName} className="h-full w-full text-xl" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">Photo de profil</p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                    id="photo-upload"
                  />
                  <button
                    type="button"
                    onClick={() => document.getElementById('photo-upload').click()}
                    className={cx(btnSecondary, 'mt-2 !px-3 !py-1.5')}
                  >
                    <Camera className="h-4 w-4" />
                    Choisir une photo
                  </button>
                </div>
              </div>

              <Input label="Nom" type="text" name="nom" value={formData.nom} onChange={handleInputChange} required />
              <Input label="Prénom" type="text" name="prenom" value={formData.prenom} onChange={handleInputChange} required />
              <Input label="Autres Prénoms" type="text" name="autres_prenom" value={formData.autres_prenom} onChange={handleInputChange} />
              <Segmented
                label="Sexe"
                required
                value={formData.sexe}
                onSelect={(v) => handleInputChange({ target: { name: 'sexe', value: v } })}
                options={[{ value: 'M', label: 'Masculin' }, { value: 'F', label: 'Féminin' }]}
              />
              <Input label="Date de naissance" type="date" name="date_naissance" value={formData.date_naissance} onChange={handleInputChange} required />
              <Input label="Lieu de naissance" type="text" name="lieu_naissance" value={formData.lieu_naissance} onChange={handleInputChange} placeholder="Ex: Abidjan, Cotonou, etc." />
              <Input label="Nationalité" type="text" name="nationalite" value={formData.nationalite} onChange={handleInputChange} required placeholder="Ex: Ivoirienne, Béninoise, etc." />
            </Section>

            <Section title="Situation familiale" description="Sert au calcul automatique de la part IGR.">
              <Select label="Situation matrimoniale" name="situation_matrimoniale" value={formData.situation_matrimoniale} onChange={handleInputChange} required>
                <option value="">Sélectionner</option>
                <option value="Célibataire">Célibataire</option>
                <option value="Marié(e)">Marié(e)</option>
                <option value="Divorcé(e)">Divorcé(e)</option>
                <option value="Veuf/Veuve">Veuf/Veuve</option>
              </Select>
              <Input label="Nombre d'enfants à charge" type="number" name="nombre_enfant_charge" value={formData.nombre_enfant_charge} onChange={handleInputChange} />
            </Section>

            <Section title="Pièce d'identité" description="Type, numéro, lieu de délivrance et validité de la pièce.">
              <Select label="Type de pièce d'identité" name="type_piece_id" value={formData.type_piece_id} onChange={handleInputChange} required>
                <option value="">Sélectionner</option>
                <option value="Passeport">Passeport</option>
                <option value="Permis de conduire">Permis de conduire</option>
                <option value="Carte d'identité nationale">Carte d'identité nationale</option>
                <option value="Autre">Autre</option>
              </Select>
              <Input label="Numéro de la pièce d'identité" type="text" name="num_piece_id" value={formData.num_piece_id} onChange={handleInputChange} required />
              <Input label="Lieu de délivrance" type="text" name="lieu_delivrance_piece" value={formData.lieu_delivrance_piece} onChange={handleInputChange} required placeholder="Ex: Cotonou, Abidjan, etc." />
              <Input label="Date d'expiration de la pièce d'Identité" type="date" name="date_expiration_piece" value={formData.date_expiration_piece} onChange={handleInputChange} required />
            </Section>

            <Section title="Statut et matricule" description="Le matricule peut être saisi ou généré selon la nomenclature.">
              <Field label="Statut (Direct/Indirect)" required>
                <div className="flex h-[38px] items-center gap-3">
                  <span className={cx('text-sm', formData.direct_indirect !== true ? 'font-semibold text-slate-900' : 'text-slate-500')}>Indirect (I)</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={formData.direct_indirect === true}
                    onClick={() => {
                      console.log('Direct/Indirect BEFORE click:', formData.direct_indirect, typeof formData.direct_indirect);
                      setFormData(prev => {
                        const newValue = !prev.direct_indirect;
                        console.log('Direct/Indirect AFTER click:', newValue, typeof newValue);
                        return { ...prev, direct_indirect: newValue };
                      });
                    }}
                    className={cx(
                      'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40 focus-visible:ring-offset-2',
                      formData.direct_indirect === true ? 'bg-blue-600' : 'bg-slate-300',
                    )}
                  >
                    <span
                      className={cx(
                        'inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out',
                        formData.direct_indirect === true ? 'translate-x-6' : 'translate-x-1',
                      )}
                    />
                  </button>
                  <span className={cx('text-sm', formData.direct_indirect === true ? 'font-semibold text-slate-900' : 'text-slate-500')}>Direct (D)</span>
                </div>
              </Field>

              <div className="sm:col-span-2">
                <Field label="Matricule" htmlFor="matricule">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      id="matricule"
                      name="matricule"
                      value={formData.matricule}
                      onChange={handleInputChange}
                      disabled={autoGenerateMatricule}
                      className={cx(controlCls, 'flex-1')}
                      placeholder={autoGenerateMatricule ? 'Généré automatiquement' : 'Saisir manuellement ou générer'}
                    />
                    {!autoGenerateMatricule && (
                      <button
                        type="button"
                        onClick={generateMatricule}
                        className={btnSecondary}
                        title="Générer selon nomenclature"
                      >
                        <RefreshCw className="h-4 w-4" />
                        Générer
                      </button>
                    )}
                  </div>
                </Field>
                <div className="mt-3 flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="autoGenerateMatricule"
                    checked={autoGenerateMatricule}
                    onChange={(e) => {
                      setAutoGenerateMatricule(e.target.checked);
                      if (e.target.checked && formData.filiale_id) {
                        generateMatricule();
                      }
                    }}
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-600"
                  />
                  <label htmlFor="autoGenerateMatricule" className="text-sm text-slate-600">
                    Générer automatiquement selon la nomenclature
                  </label>
                </div>
              </div>
            </Section>
          </>
        );

      case 2:
        return (
          <>
            <Section title="Coordonnées" description="Pour joindre l'employé et lui adresser ses documents.">
              <Input label="Numéro de téléphone" type="tel" name="numtel" value={formData.numtel} onChange={handleInputChange} required />
              <Input label="Adresse mail" type="email" name="adressemail" value={formData.adressemail} onChange={handleInputChange} required />
              <Textarea label="Adresse complète" name="adresse_complete" value={formData.adresse_complete} onChange={handleInputChange} rows="3" required className="sm:col-span-2" />
            </Section>
            <Section title="Urgence et protection sociale">
              <Input label="Personne à contacter" type="text" name="personne_contact" value={formData.personne_contact} onChange={handleInputChange} className="sm:col-span-2" />
              <Input label="Numéro CNPS (CI) / CNSS (autres)" type="text" name="numero_cnss" value={formData.numero_cnss} onChange={handleInputChange} />
            </Section>
          </>
        );

      case 3:
        return (
          <>
            <Section title="Poste" description="Arrivée, rattachement et fonction de l'employé.">
              <Input label="Date d'entrée dans l'entreprise" type="date" name="date_entree" value={formData.date_entree} onChange={handleInputChange} required />
              <Select label="Département" name="departement_id" value={formData.departement_id} onChange={handleInputChange} required>
                <option value="">Sélectionner</option>
                {dropdownData.departements && dropdownData.departements.map((d) => (
                  <option key={d.iddepartement} value={d.iddepartement}>{d.libelle_departement}</option>
                ))}
              </Select>
              <Select label="Business Unit" name="business_unit_id" value={formData.business_unit_id} onChange={handleInputChange}>
                <option value="">Sélectionner</option>
                {dropdownData.businessUnits && dropdownData.businessUnits.map((bu) => (
                  <option key={bu.idbusiness_unit} value={bu.idbusiness_unit}>{bu.libelle}</option>
                ))}
              </Select>
              <Select label="Fonction (Titre)" name="titre_id" value={formData.titre_id} onChange={handleInputChange} required>
                <option value="">Sélectionner</option>
                {dropdownData.titres && dropdownData.titres.map((t) => (
                  <option key={t.idtitre} value={t.idtitre}>{t.nom_developpe}</option>
                ))}
              </Select>
              <Select label="Grade" name="grade_id" value={formData.grade_id} onChange={handleInputChange}>
                <option value="">Sélectionner</option>
                {dropdownData.grades && dropdownData.grades.map((g) => (
                  <option key={g.idgrade} value={g.idgrade}>{g.libelle}</option>
                ))}
              </Select>
              <Select label="Statut du personnel" name="statut" value={formData.statut} onChange={handleInputChange} required>
                <option value="">Sélectionner</option>
                <option value="Contractuel">Contractuel</option>
                <option value="Stagiaire">Stagiaire</option>
                <option value="Agent en formation">Agent en formation</option>
              </Select>
              <Input label="Emplacement" type="text" name="emplacement" value={formData.emplacement} onChange={handleInputChange} required className="sm:col-span-2" />
            </Section>

            <Section title="Activité et rémunération" description="Le portefeuille client dépend du type d'activité choisi.">
              <Select label="Type d'activité" name="activite_id" value={formData.activite_id} onChange={handleInputChange} required>
                <option value="">Sélectionner</option>
                {dropdownData.activites && dropdownData.activites.map((a) => (
                  <option key={a.idactivites} value={a.idactivites}>{a.Libelle_activite}</option>
                ))}
              </Select>
              <Select label="Portefeuille client (Campagne)" name="idcampagne" value={formData.idcampagne} onChange={handleInputChange} required>
                <option value="">Sélectionner</option>
                {filteredCampagnes && filteredCampagnes.map((c) => (
                  <option key={c.idcampagne} value={c.idcampagne}>{c.Libelle_campagne}</option>
                ))}
              </Select>
              <Select label="Type personnel (Mode de rémunération)" name="type_personnel_id" value={formData.type_personnel_id} onChange={handleInputChange} required>
                <option value="">Sélectionner</option>
                {dropdownData.typesPersonnel && dropdownData.typesPersonnel.map((tp) => (
                  <option key={tp.idtype_personnel} value={tp.idtype_personnel}>{tp.libelle}</option>
                ))}
              </Select>
              <Select label="Marque" name="marque_id" value={formData.marque_id} onChange={handleInputChange}>
                <option value="">Sélectionner</option>
                {dropdownData.marques && dropdownData.marques.map((m) => (
                  <option key={m.id} value={m.id}>{m.libelle}</option>
                ))}
              </Select>
              <Select
                label="Grille"
                name="grille_personnel_id"
                value={formData.grille_personnel_id || ''}
                onChange={(e) => {
                  console.log('Grille BEFORE change:', formData.grille_personnel_id);
                  console.log('Grille selected value:', e.target.value);
                  handleInputChange(e);
                  console.log('Grille AFTER change:', formData.grille_personnel_id);
                }}
              >
                <option value="">Sélectionner</option>
                {dropdownData.grillesPersonnel && dropdownData.grillesPersonnel.map((gp, index) => (
                  <option key={`grille-${gp.idgrille_personnel}-${index}`} value={gp.idgrille_personnel}>{gp.Libelle_categorie}</option>
                ))}
              </Select>
            </Section>
          </>
        );

      case 4:
        return (
          <Section title="Ligne hiérarchique" description="Tapez un nom pour filtrer la liste, puis sélectionnez la personne.">
            <Field label="Manager hiérarchique" required htmlFor="manager_hierarchique_id">
              <div className="space-y-2">
                <SearchBox
                  placeholder="Rechercher un employé..."
                  value={managerSearch}
                  onChange={(e) => setManagerSearch(e.target.value)}
                />
                <SelectControl id="manager_hierarchique_id" name="manager_hierarchique_id" value={formData.manager_hierarchique_id} onChange={handleInputChange} required>
                  <option value="">Sélectionner</option>
                  {filteredManagers.map((m) => (
                    <option key={m.idpersonnel} value={m.idpersonnel}>{m.prenom} {m.nom}</option>
                  ))}
                </SelectControl>
              </div>
            </Field>

            <Input label="Fonction du manager" type="text" name="fonction_manager" value={formData.fonction_manager} onChange={handleInputChange} />

            <Field label="N+2" htmlFor="n2_id">
              <div className="space-y-2">
                <SearchBox
                  placeholder="Rechercher un employé..."
                  value={n2Search}
                  onChange={(e) => setN2Search(e.target.value)}
                />
                <SelectControl id="n2_id" name="n2_id" value={formData.n2_id} onChange={handleInputChange}>
                  <option value="">Sélectionner</option>
                  {filteredN2.map((m) => (
                    <option key={m.idpersonnel} value={m.idpersonnel}>{m.prenom} {m.nom}</option>
                  ))}
                </SelectControl>
              </div>
            </Field>
          </Section>
        );

      case 5:
        return (
          <>
            <Section title="Formation" description="Dernier parcours d'études de l'employé.">
              <Input label="Domaine d'étude" type="text" name="domaine_etude" value={formData.domaine_etude} onChange={handleInputChange} required />
              <Select label="Niveau d'étude" name="niveau_etude" value={formData.niveau_etude} onChange={handleInputChange} required>
                <option value="">Sélectionner</option>
                {dropdownData.niveauxEtude && dropdownData.niveauxEtude.map((ne) => (
                  <option key={ne.id} value={ne.libelle}>{ne.libelle}</option>
                ))}
              </Select>
            </Section>

            <Section title="Situation administrative et fiscale">
              <Input label="Part IGR (calculé automatiquement)" type="text" name="part_igr" value={formData.part_igr} readOnly />
              <Segmented
                label="Domiciliation"
                value={formData.domiciliation}
                onSelect={(v) => handleInputChange({ target: { name: 'domiciliation', value: v } })}
                options={[{ value: 'Non', label: 'Non' }, { value: 'Oui', label: 'Oui' }]}
              />
              <Input label="Numéro IFU (Bénin uniquement)" type="text" name="numero_ifu" value={formData.numero_ifu} onChange={handleInputChange} />
            </Section>
          </>
        );

      case 6: {
        const foundFiliale = dropdownData.filiales?.find(f => f.idfiliale === formData.filiale_id);
        const foundDepartement = dropdownData.departements?.find(d => String(d.iddepartement) === String(formData.departement_id));
        const foundTitre = dropdownData.titres?.find(t => String(t.idtitre) === String(formData.titre_id));
        const foundTypePersonnel = dropdownData.typesPersonnel?.find(tp => String(tp.idtype_personnel) === String(formData.type_personnel_id));
        const foundCampagne = filteredCampagnes?.find(c => String(c.idcampagne) === String(formData.idcampagne));
        const foundMarque = dropdownData.marques?.find(m => String(m.id) === String(formData.marque_id));
        const foundGrille = dropdownData.grillesPersonnel?.find(g => String(g.idgrille_personnel) === String(formData.grille_personnel_id));

        return (
          <>
            <SummaryBlock title="Identité">
              <SummaryRow label="Nom">{formData.nom || 'Non renseigné'} {formData.prenom || ''}</SummaryRow>
              <SummaryRow label="Matricule">{formData.matricule || 'Non défini'}</SummaryRow>
            </SummaryBlock>

            <SummaryBlock title="Affectation">
              <SummaryRow label="Filiale">{foundFiliale?.libelle || currentFiliale?.libelle || 'Non sélectionné'}</SummaryRow>
              <SummaryRow label="Département">{dropdownData.departements?.find(d => String(d.iddepartement) === String(formData.departement_id))?.libelle_departement || 'Non sélectionné'}</SummaryRow>
              <SummaryRow label="Fonction">{dropdownData.titres?.find(t => String(t.idtitre) === String(formData.titre_id))?.nom_developpe || 'Non sélectionné'}</SummaryRow>
              <SummaryRow label="Type de contrat">{formData.type_contrat || 'Non défini'}</SummaryRow>
              <SummaryRow label="Type personnel">{dropdownData.typesPersonnel?.find(tp => String(tp.idtype_personnel) === String(formData.type_personnel_id))?.libelle || 'Non sélectionné'}</SummaryRow>
              <SummaryRow label="Grille">{dropdownData.grillesPersonnel?.find(g => String(g.idgrille_personnel) === String(formData.grille_personnel_id))?.Libelle_categorie || 'Non sélectionné'}</SummaryRow>
              <SummaryRow label="Campagne">{filteredCampagnes?.find(c => String(c.idcampagne) === String(formData.idcampagne))?.Libelle_campagne || 'Non sélectionné'}</SummaryRow>
              <SummaryRow label="Marque">{dropdownData.marques?.find(m => String(m.id) === String(formData.marque_id))?.libelle || 'Non sélectionné'}</SummaryRow>
              <SummaryRow label="Date d'entrée">{formData.date_entree || 'Non renseignée'}</SummaryRow>
            </SummaryBlock>

            <SummaryBlock title="Contact">
              <SummaryRow label="Email">{formData.adressemail || 'Non renseigné'}</SummaryRow>
              <SummaryRow label="Téléphone">{formData.numtel || 'Non renseigné'}</SummaryRow>
            </SummaryBlock>
          </>
        );
      }

      default:
        return null;
    }
  };

  const shell = (
    <div
      className={cx(
        'flex w-full overflow-hidden bg-white',
        isModal
          ? 'h-[min(90vh,820px)] max-w-6xl rounded-2xl shadow-2xl ring-1 ring-black/10'
          : 'min-h-[640px] rounded-2xl border border-slate-200',
      )}
    >
      {/* Barre latérale : titre, aperçu et étapes */}
      <aside className="hidden w-72 shrink-0 flex-col border-r border-slate-200 bg-slate-50 p-6 md:flex">
        <h2 className="text-base font-semibold text-slate-900">
          {editData ? 'Modifier le personnel' : 'Nouveau personnel'}
        </h2>

        <div className="mt-5 flex items-center gap-3">
          <Avatar src={formData.photo_profil} name={fullName} className="h-11 w-11 shrink-0 text-sm" />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-slate-900">{fullName || 'Nom à renseigner'}</p>
            <p className="truncate font-mono text-xs text-slate-500">{formData.matricule || 'Matricule à définir'}</p>
          </div>
        </div>

        <nav aria-label="Étapes" className="mt-8">
          <ol className="space-y-1">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isCompleted = currentStep > step.id;
              const isCurrent = currentStep === step.id;
              return (
                <li key={step.id} className="relative" aria-current={isCurrent ? 'step' : undefined}>
                  {index < steps.length - 1 && (
                    <span
                      aria-hidden="true"
                      className={cx('absolute left-7 top-[42px] -bottom-[14px] w-px', isCompleted ? 'bg-emerald-300' : 'bg-slate-200')}
                    />
                  )}
                  <div className={cx('relative flex items-center gap-3 rounded-lg px-3 py-2.5', isCurrent && 'bg-white shadow-sm ring-1 ring-slate-200')}>
                    <span
                      className={cx(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                        isCompleted
                          ? 'bg-emerald-600 text-white'
                          : isCurrent
                            ? 'bg-blue-600 text-white ring-4 ring-blue-100'
                            : 'bg-white text-slate-400 ring-1 ring-inset ring-slate-300',
                      )}
                    >
                      {isCompleted ? <Check className="h-4 w-4" /> : <StepIcon className="h-4 w-4" />}
                    </span>
                    <span className={cx('text-sm font-medium', isCurrent ? 'text-slate-900' : isCompleted ? 'text-slate-700' : 'text-slate-500')}>
                      {step.title}
                    </span>
                  </div>
                </li>
              );
            })}
          </ol>
        </nav>
      </aside>

      {/* Contenu */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4">
          <div>
            <p className="text-xs font-medium text-blue-700 md:hidden">Étape {currentStep}/6</p>
            <h3 className="text-xl font-semibold text-slate-900">{stepInfo[currentStep].title}</h3>
            <p className="text-sm text-slate-500">{stepInfo[currentStep].subtitle}</p>
          </div>
          {isModal && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Fermer"
              className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </header>
        <div className="h-1 bg-slate-100 md:hidden">
          <div className="h-full bg-blue-600 transition-all" style={{ width: `${(currentStep / 6) * 100}%` }} />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
          {renderStep()}
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-slate-200 bg-white px-6 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className={btnSecondary}
            >
              <ChevronLeft className="h-4 w-4" />
              Précédent
            </button>
            <span className="hidden text-sm font-medium text-slate-500 sm:inline">Étape {currentStep}/6</span>
          </div>

          {currentStep === 6 ? (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className={btnPrimary}
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  Sauvegarde...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  {editData ? 'Mettre à jour' : 'Créer l\'employé'}
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleNext}
              className={btnPrimary}
            >
              Suivant
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </footer>
      </div>
    </div>
  );

  return isModal ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
      {shell}
    </div>
  ) : shell;
};

export default PersonnelWizard;